import { Droplets, ExternalLink, LoaderCircle, Wallet } from 'lucide-react';
import { useEffect, useState } from 'react';
import { formatUnits } from 'viem';
import { ApiError, api } from '../lib/api';
import { useWallet } from '../state/WalletContext';

interface FaucetStatus {
  enabled: boolean;
  chainId?: number;
  tokenAddress?: string;
  btcbAmountAtomic?: string;
  bnbAmountWei?: string;
  cooldownSeconds?: number;
}

interface FaucetDrip {
  recipient: string;
  tokenTxHash: string;
  gasTxHash: string | null;
  nextEligibleAt: string;
}

const explorerUrl = 'https://testnet.bscscan.com';

/**
 * Testnet-only "get test funds" card. Hidden on mainnet and whenever the API reports the faucet
 * disabled. One click mints mock BTCB and sends a small tBNB gas drip to the connected wallet.
 */
export function FaucetCard({ compact = false }: { compact?: boolean }) {
  const wallet = useWallet();
  const [status, setStatus] = useState<FaucetStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [drip, setDrip] = useState<FaucetDrip | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (wallet.expectedChainId !== 97) return;
    let cancelled = false;
    api<FaucetStatus>('/api/public/faucet')
      .then((value) => { if (!cancelled) setStatus(value); })
      .catch(() => { if (!cancelled) setStatus({ enabled: false }); });
    return () => { cancelled = true; };
  }, [wallet.expectedChainId]);

  if (wallet.expectedChainId !== 97 || !status?.enabled) return null;

  const btcb = status.btcbAmountAtomic ? formatUnits(BigInt(status.btcbAmountAtomic), 18) : '0.001';
  const bnb = status.bnbAmountWei ? formatUnits(BigInt(status.bnbAmountWei), 18) : '0';

  const claim = async () => {
    if (!wallet.address) {
      await wallet.connect();
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await api<FaucetDrip>('/api/public/faucet', { method: 'POST', body: JSON.stringify({ address: wallet.address }) });
      setDrip(result);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'The faucet request failed. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`faucet-card ${compact ? 'compact' : ''}`} role="region" aria-label="Testnet faucet">
      <span className="faucet-icon"><Droplets size={18} aria-hidden /></span>
      {/* The title sits beside the icon; the copy and the action span the card's full width below,
          so a narrow container (the ~285px join aside) never squeezes the paragraph into a ribbon. */}
      <strong className="faucet-title">Need test funds?</strong>
      <div className="faucet-body">
        <p>
          Get <b>{btcb} mock BTCB</b>{Number(bnb) > 0 ? <> and <b>{bnb} tBNB</b> for gas</> : null} sent to your connected wallet. {(status.cooldownSeconds ?? 0) > 0 ? `One claim per wallet every ${(status.cooldownSeconds ?? 0) < 3_600 ? `${Math.round((status.cooldownSeconds ?? 0) / 60)} minutes` : `${Math.round((status.cooldownSeconds ?? 0) / 3_600)} hours`}.` : 'Claim as often as you need.'} Testnet only.
        </p>
        {drip ? (
          <div className="faucet-result" role="status">
            <span>Sent to {drip.recipient.slice(0, 6)}…{drip.recipient.slice(-4)}.</span>
            <a href={`${explorerUrl}/tx/${drip.tokenTxHash}`} target="_blank" rel="noreferrer">BTCB mint <ExternalLink size={12} /></a>
            {drip.gasTxHash ? <a href={`${explorerUrl}/tx/${drip.gasTxHash}`} target="_blank" rel="noreferrer">Gas drip <ExternalLink size={12} /></a> : null}
          </div>
        ) : null}
        {error ? <div className="faucet-error" role="alert">{error}</div> : null}
      </div>
      {/* With no cooldown a wallet may claim repeatedly, so the button re-enables after a drip
          instead of locking on "Funds sent" until the page is reloaded. */}
      <button className="button secondary small" type="button" disabled={busy || (Boolean(drip) && (status.cooldownSeconds ?? 0) > 0)} onClick={() => void claim()}>
        {busy ? <LoaderCircle className="spin" size={15} /> : wallet.address ? <Droplets size={15} /> : <Wallet size={15} />}
        {busy ? 'Sending…' : drip ? ((status.cooldownSeconds ?? 0) > 0 ? 'Funds sent' : 'Claim again') : wallet.address ? 'Get test BTCB' : 'Connect wallet'}
      </button>
    </div>
  );
}
